import { z } from 'zod';
/**
 * Коды ошибок API. Сервер не возвращает готовых фраз: клиент подставляет текст из словаря
 * выбранного языка, поэтому одна и та же ошибка читается по-русски, по-английски, по-немецки
 * и по-испански без участия сервера.
 */
export const ERROR_CODES = [
    // Авторизация и сессия
    'UNAUTHENTICATED',
    'SESSION_EXPIRED',
    'FORBIDDEN',
    'OAUTH_STATE_INVALID',
    'OAUTH_EXCHANGE_FAILED',
    'NO_ACCESSIBLE_SITES',
    // Интеграция с Jira
    'JIRA_DISCONNECTED',
    'JIRA_FORBIDDEN',
    'JIRA_ISSUE_GONE',
    'JIRA_RATE_LIMITED',
    'JIRA_UNAVAILABLE',
    'JIRA_BAD_RESPONSE',
    'JIRA_FIELD_UNAVAILABLE',
    /**
     * Jira отклонила запрос как некорректный. Отдельный код нужен, потому что причина здесь
     * на нашей стороне, а не в недоступности сервиса: например, эндпоинт /search/jql требует
     * ограниченный JQL и отказывает запросу с одним лишь order by.
     */
    'JIRA_QUERY_INVALID',
    /**
     * Экран создания задачи требует полей, которых нет в форме Jisy (компоненты, версии, свои
     * поля проекта). Отдельный код нужен, чтобы предложить завести задачу в Jira, а не показать
     * общий отказ: запрос корректен, не хватает именно значений.
     */
    'JIRA_REQUIRED_FIELDS',
    // Интеграция с Fathom
    /** Ключ Fathom не заведён: раздел встреч предлагает подключить его в настройках. */
    'FATHOM_NOT_CONNECTED',
    /** Fathom отклонила ключ. Отдельный код от «не подключено»: ключ есть, но больше не годится. */
    'FATHOM_UNAUTHORIZED',
    'FATHOM_RATE_LIMITED',
    'FATHOM_UNAVAILABLE',
    'FATHOM_BAD_RESPONSE',
    // Прикладные
    'VALIDATION_FAILED',
    'NOT_FOUND',
    'ISSUE_CONFLICT',
    /**
     * У задачи есть подзадачи, а удаление их уносит. Отдельный код нужен, чтобы спросить
     * согласие с числом подзадач: удаление необратимо, и «просто повторить» здесь мало.
     */
    'ISSUE_HAS_SUBTASKS',
    /**
     * Jira не перенесла задачу между уровнями (задача ⇄ подзадача): отказ пришёл из фонового
     * задания переноса. Причина уходит в лог — Jira пишет её на языке сайта.
     */
    'ISSUE_MOVE_FAILED',
    /**
     * Перенос поставлен, но Jira не закончила его, пока шёл ответ. Отдельный код от отказа:
     * задача перенесётся сама, и человеку нужно лишь обновить карточку позже, а не повторять.
     */
    'ISSUE_MOVE_PENDING',
    'PLAN_CONFLICT',
    /**
     * Картинка в заметке не принята: не изображение поддерживаемого типа или тяжелее потолка.
     * Тип определяется по сигнатуре файла, а не по заголовку запроса, поэтому переименованный
     * файл сюда тоже попадает.
     */
    'NOTE_IMAGE_REJECTED',
    /**
     * Вложение не принято: пустой файл или тяжелее потолка. Отдельный код от «Jira отказала»
     * нужен, чтобы назвать в тексте предельный размер, — до Jira такой файл даже не уезжает.
     */
    'ATTACHMENT_REJECTED',
    /**
     * Раздел не создан: Jira отвергла ключ или название. Коды раздельные, потому что чинить их
     * человеку приходится по-разному — и оба поля стоят рядом в одной форме.
     */
    'PROJECT_KEY_TAKEN',
    'PROJECT_NAME_TAKEN',
    /** Прочий отказ Jira при создании раздела: причина уходит в лог, человеку — общий текст. */
    'PROJECT_CREATE_REJECTED',
    /**
     * Приложению не выдано право создавать проекты. Отдельный код от «доступ отозван»: токен
     * действует, не хватает именно скоупа `manage:jira-configuration`, и чинится это переподключением
     * Jira, а не ожиданием.
     */
    'PROJECT_SCOPE_MISSING',
    'RATE_LIMITED',
    'INTERNAL',
];
export const ErrorCodeSchema = z.enum(ERROR_CODES);
/**
 * Тело ответа при ошибке. `params` — значения для подстановки в перевод
 * (например, имя редактора при конфликте версий), `requestId` показывается в интерфейсе
 * мелким моноширинным текстом, как в макете состояний.
 */
export const ApiErrorSchema = z.object({
    error: z.object({
        code: ErrorCodeSchema,
        params: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
        requestId: z.string().optional(),
        /** Сообщение для разработчика: пишется в лог, в интерфейсе не показывается. */
        detail: z.string().optional(),
    }),
});
/** HTTP-статус, соответствующий коду ошибки. */
export const ERROR_HTTP_STATUS = {
    UNAUTHENTICATED: 401,
    SESSION_EXPIRED: 401,
    FORBIDDEN: 403,
    OAUTH_STATE_INVALID: 400,
    OAUTH_EXCHANGE_FAILED: 502,
    NO_ACCESSIBLE_SITES: 422,
    JIRA_DISCONNECTED: 409,
    JIRA_FORBIDDEN: 403,
    JIRA_ISSUE_GONE: 410,
    JIRA_RATE_LIMITED: 429,
    JIRA_UNAVAILABLE: 502,
    JIRA_BAD_RESPONSE: 502,
    JIRA_FIELD_UNAVAILABLE: 422,
    JIRA_QUERY_INVALID: 400,
    JIRA_REQUIRED_FIELDS: 422,
    FATHOM_NOT_CONNECTED: 409,
    FATHOM_UNAUTHORIZED: 409,
    FATHOM_RATE_LIMITED: 429,
    FATHOM_UNAVAILABLE: 502,
    FATHOM_BAD_RESPONSE: 502,
    VALIDATION_FAILED: 400,
    NOT_FOUND: 404,
    ISSUE_CONFLICT: 409,
    ISSUE_HAS_SUBTASKS: 409,
    ISSUE_MOVE_FAILED: 422,
    // Ответ не дождался Jira — по смыслу это таймаут шлюза, хотя сам перенос ещё идёт.
    ISSUE_MOVE_PENDING: 504,
    PLAN_CONFLICT: 409,
    NOTE_IMAGE_REJECTED: 415,
    ATTACHMENT_REJECTED: 413,
    PROJECT_KEY_TAKEN: 409,
    PROJECT_NAME_TAKEN: 409,
    PROJECT_CREATE_REJECTED: 422,
    PROJECT_SCOPE_MISSING: 403,
    RATE_LIMITED: 429,
    INTERNAL: 500,
};
/**
 * Ошибки, при которых интерфейс показывает данные из кэша и предлагает повторить запрос,
 * а не пустой экран (карточка «Jira не ответила» в макете состояний).
 */
export const STALE_CACHE_FALLBACK_CODES = [
    'JIRA_UNAVAILABLE',
    'JIRA_RATE_LIMITED',
    'JIRA_BAD_RESPONSE',
];
//# sourceMappingURL=errors.js.map